import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizationsList,
  useUpdateOrganization,
} from '../../../hooks/queries/useOrganizations';
import { toaster } from '../../ui/toaster';
import type { StatusType } from '@themes/aegix';
import type { District, OrganizationWriteInput } from '../../../services';
import {
  DIRECT_CHAT_UNREAD_NOTIFICATION_DELAY_DEFAULT,
  ORGANIZATION_AUTH_DAYS_DEFAULT,
  START_BUTTON_LABEL_KEYS,
  type OrganizationFormValues,
  type OrganizationStatus,
} from '../../../validations/organization.validation';

export interface OrganizationRow {
  id: number;
  public_id: string;
  name: string;
  domain: string;
  industry: string;
  type: string;
  status: StatusType;
  raw: District;
}

type StatusFilter = 'all' | OrganizationStatus;

/**
 * Map the canonical Django District.status (onboard | online | offline) to
 * the `@themes/aegix` StatusBadge palette. Onboard reads as "in progress" so
 * `pending` fits; online is the live state (`active`); offline is the
 * disabled / paused state (`inactive`).
 */
function toStatusType(status: string | undefined): StatusType {
  switch (status) {
    case 'online':
      return 'active';
    case 'onboard':
      return 'pending';
    case 'offline':
    default:
      return 'inactive';
  }
}

/**
 * High-entropy random string suitable for HS256 shared secret on the
 * District JWT setting. 32 bytes of CSPRNG → 256 bits, base64-encoded.
 * RS256 public keys are pasted by the operator instead of generated.
 */
function generateJwtSecret(): string {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) return '';
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function joinEmails(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') return value;
  return '';
}

/**
 * District JSONB flags are serialized as strings on the wire (the
 * `JSONBFieldAttributeSerializerMixin` returns them coerced). Treat anything
 * matching "true" (any case) as `true`, else `false`.
 */
function asBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
}

/**
 * Surface a backend error's `detail` message — DRF returns `{detail: "..."}`
 * for 403 / permission failures, including the licensing-guard rejection
 * on licensed sites — so the user sees why a delete failed.
 * `unwrap` (sites.service) throws `result.error` (the parsed body), so the
 * thrown value carries `detail` when present. Falls back to a generic msg.
 */
function errorDetail(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const detail = (err as { detail?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
  }
  return fallback;
}

function buildDefaultValues(row?: OrganizationRow): OrganizationFormValues {
  if (!row) {
    // Defaults mirror the Django model (server/apps/schools/models.py),
    // except industry:
    // - industry → '' (unset). Left for the operator to choose: an org with
    //   no industry key gets the always-operating calendar (ADR-126
    //   Amendment 3), and pre-filling k12 would pick school hours for them.
    // - type → ADMIN
    // - status → ONBOARD
    // - is_silent_drill_default → True
    // - auth_expiry_days / auth_refresh_days → ONE_YEAR (365)
    // - direct_chat_unread_notification_delay → 0 (disabled)
    // - jwt_secret → auto-generated CSPRNG value for HS256
    return {
      id: undefined,
      name: '',
      domain: '',
      industry: '',
      type: 'admin',
      status: 'onboard',
      system_admin_email: '',
      start_alert_button_label: '',
      start_drill_button_label: '',
      start_report_button_label: '',
      auth_expiry_days: String(ORGANIZATION_AUTH_DAYS_DEFAULT),
      auth_refresh_days: String(ORGANIZATION_AUTH_DAYS_DEFAULT),
      direct_chat_unread_notification_delay: String(
        DIRECT_CHAT_UNREAD_NOTIFICATION_DELAY_DEFAULT,
      ),
      jwt_secret: generateJwtSecret(),
      is_sandbox: false,
      is_silent_drill_default: true,
      use_missing_person_chat: false,
      can_share_alerts: false,
      coverage_type: '',
      latitude: '',
      longitude: '',
      service_radius_km: '',
      area_type: '',
      area_name: '',
    };
  }
  const raw = row.raw;
  const status = (raw.status as OrganizationStatus) ?? 'onboard';
  const extra = raw as unknown as {
    can_share_alerts?: boolean | string;
    direct_chat_unread_notification_delay?: number | string;
  };
  return {
    id: row.id,
    name: row.name ?? '',
    domain: row.domain ?? '',
    industry: (row.industry as OrganizationFormValues['industry']) ?? '',
    type: (row.type as OrganizationFormValues['type']) ?? '',
    status,
    system_admin_email: joinEmails(raw.system_admin_email),
    start_alert_button_label: raw.start_alert_button_label ?? '',
    start_drill_button_label: raw.start_drill_button_label ?? '',
    start_report_button_label: raw.start_report_button_label ?? '',
    auth_expiry_days: raw.auth_expiry_days ? String(raw.auth_expiry_days) : '',
    auth_refresh_days: raw.auth_refresh_days
      ? String(raw.auth_refresh_days)
      : '',
    direct_chat_unread_notification_delay:
      extra.direct_chat_unread_notification_delay !== undefined &&
      extra.direct_chat_unread_notification_delay !== null
        ? String(extra.direct_chat_unread_notification_delay)
        : '',
    jwt_secret: raw.jwt_secret ?? '',
    is_sandbox: asBool(raw.is_sandbox),
    is_silent_drill_default: asBool(raw.is_silent_drill_default),
    use_missing_person_chat: asBool(raw.use_missing_person_chat),
    can_share_alerts: asBool(extra.can_share_alerts),
    coverage_type: raw.coverage_type ?? '',
    latitude: raw.latitude !== undefined && raw.latitude !== null ? String(raw.latitude) : '',
    longitude: raw.longitude !== undefined && raw.longitude !== null ? String(raw.longitude) : '',
    service_radius_km: raw.service_radius_km !== undefined && raw.service_radius_km !== null ? String(raw.service_radius_km) : '',
    area_type: raw.area_type ?? '',
    area_name: raw.area_name ?? '',
  };
}

function parseEmailList(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseNumber(value: string | undefined): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function toWriteInput(values: OrganizationFormValues): OrganizationWriteInput {
  const out: Record<string, unknown> = {
    name: values.name,
  };

  if (values.domain !== undefined) out.domain = values.domain;
  if (values.industry) out.industry = values.industry;
  if (values.type) out.type = values.type;
  if (values.status) out.status = values.status;
  if (values.system_admin_email !== undefined) {
    out.system_admin_email = parseEmailList(values.system_admin_email);
  }
  // "" is not a valid label — a button that renders needs a name, so
  // `DistrictSerializer` rejects blank. Omit a blank box rather than sending
  // "", which means "leave this label alone". On create that lands the org on the
  // model default, which Django translates — so don't substitute the
  // placeholder text here, or non-English orgs get an English label written
  // into their JSONB.
  for (const key of START_BUTTON_LABEL_KEYS) {
    const label = values[key]?.trim();
    if (label) out[key] = label;
  }
  const expiryDays = parseNumber(values.auth_expiry_days);
  if (expiryDays !== undefined) out.auth_expiry_days = expiryDays;
  const refreshDays = parseNumber(values.auth_refresh_days);
  if (refreshDays !== undefined) out.auth_refresh_days = refreshDays;
  const directChatDelay = parseNumber(values.direct_chat_unread_notification_delay);
  if (directChatDelay !== undefined) {
    out.direct_chat_unread_notification_delay = directChatDelay;
  }
  if (values.jwt_secret !== undefined && values.jwt_secret !== '') {
    out.jwt_secret = values.jwt_secret;
  }
  if (values.is_sandbox !== undefined) out.is_sandbox = values.is_sandbox;
  if (values.is_silent_drill_default !== undefined) {
    out.is_silent_drill_default = values.is_silent_drill_default;
  }
  if (values.use_missing_person_chat !== undefined) {
    out.use_missing_person_chat = values.use_missing_person_chat;
  }
  if (values.can_share_alerts !== undefined) {
    out.can_share_alerts = values.can_share_alerts;
  }
  if (values.coverage_type === 'point_radius') {
    out.coverage_type = 'point_radius';
    const latitude = parseNumber(values.latitude);
    out.latitude = latitude !== undefined ? latitude : null;
    const longitude = parseNumber(values.longitude);
    out.longitude = longitude !== undefined ? longitude : null;
    const serviceRadiusKm = parseNumber(values.service_radius_km);
    out.service_radius_km = serviceRadiusKm !== undefined ? serviceRadiusKm : null;
    out.area_type = null;
    out.area_name = null;
  } else if (values.coverage_type === 'admin_area') {
    out.coverage_type = 'admin_area';
    out.area_type = values.area_type || null;
    out.area_name = values.area_name || null;
    out.latitude = null;
    out.longitude = null;
    out.service_radius_km = null;
  } else {
    out.coverage_type = null;
    out.latitude = null;
    out.longitude = null;
    out.service_radius_km = null;
    out.area_type = null;
    out.area_name = null;
  }

  return out as OrganizationWriteInput;
}

export function useOrganizationsContentData() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState<OrganizationRow | undefined>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [actionMode, setActionMode] = useState<'add' | 'edit' | ''>('');
  const [sortColumn, setSortColumn] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const drawerContentRef = useRef<HTMLDivElement | null>(null);

  const createMutation = useCreateOrganization();
  const updateMutation = useUpdateOrganization();
  const deleteMutation = useDeleteOrganization();

  const ordering = sortColumn
    ? `${sortDirection === 'desc' ? '-' : ''}${sortColumn}`
    : undefined;

  const { data, isLoading, error } = useOrganizationsList({
    page: currentPage,
    page_size: pageSize,
    query: searchValue || undefined,
    ordering,
  });

  const organizations = useMemo<OrganizationRow[]>(() => {
    const results = data?.results ?? [];
    const mapped: OrganizationRow[] = results.map((org) => ({
      id: org.id,
      public_id: org.public_id,
      name: org.name ?? '',
      domain: org.domain ?? '',
      industry: (org.industry as string | undefined) ?? '',
      type: org.type ?? '',
      status: toStatusType(org.status),
      raw: org,
    }));
    if (statusFilter === 'all') return mapped;
    // Toggle filter compares against the canonical raw status from Django
    // (onboard | online | offline), not the mapped StatusBadge token.
    return mapped.filter((o) => o.raw.status === statusFilter);
  }, [data, statusFilter]);

  const filteredOrganizations = useMemo(() => {
    const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
    if (activeFilters.length === 0) return organizations;
    return organizations.filter((row) =>
      activeFilters.every(([col, val]) => {
        const cellValue = String(
          (row as unknown as Record<string, unknown>)[col] ?? '',
        ).toLowerCase();
        return cellValue.includes(val.toLowerCase());
      }),
    );
  }, [organizations, filterValues]);

  const totalItems = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, statusFilter, pageSize]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((prev) =>
          prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
        );
        if (sortDirection === 'desc') setSortColumn(undefined);
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn, sortDirection],
  );

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setActionMode('');
    setCurrentRow(undefined);
    setDeleteDialogOpen(false);
    setDeleteConfirmText('');
  }, []);

  const openDeleteDialog = useCallback(() => {
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteConfirmText('');
  }, []);

  const handleDelete = useCallback(async () => {
    if (!currentRow) return;
    // Defense-in-depth: the dialog disables Delete until the typed name
    // matches, but never let a mismatched confirmation through.
    if (deleteConfirmText.trim() !== currentRow.name) return;
    try {
      await deleteMutation.mutateAsync(currentRow.id);
      toaster.create({
        title: `${currentRow.name} has been deleted`,
        type: 'success',
      });
      setDeleteDialogOpen(false);
      setDeleteConfirmText('');
      handleDrawerClose();
    } catch (err) {
      toaster.create({
        title: errorDetail(err, 'Failed to delete organization'),
        type: 'error',
      });
    }
  }, [currentRow, deleteConfirmText, deleteMutation, handleDrawerClose]);

  const openAddDrawer = useCallback(() => {
    setActionMode('add');
    setCurrentRow(undefined);
    setDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback((row: OrganizationRow) => {
    setActionMode('edit');
    setCurrentRow(row);
    setDrawerOpen(true);
  }, []);

  const organizationFormDefaultValues = useMemo(
    () => buildDefaultValues(actionMode === 'edit' ? currentRow : undefined),
    [actionMode, currentRow],
  );

  const handleSubmit = useCallback(
    async (values: OrganizationFormValues) => {
      try {
        const payload = toWriteInput(values);
        if (actionMode === 'edit' && currentRow) {
          await updateMutation.mutateAsync({ id: currentRow.id, data: payload });
          toaster.create({
            title: 'Organization has been updated',
            type: 'success',
          });
        } else {
          await createMutation.mutateAsync(payload);
          toaster.create({
            title: 'Organization has been created',
            type: 'success',
          });
        }
        handleDrawerClose();
      } catch {
        toaster.create({
          title:
            actionMode === 'edit'
              ? 'Failed to update organization'
              : 'Failed to create organization',
          type: 'error',
        });
      }
    },
    [
      actionMode,
      currentRow,
      createMutation,
      updateMutation,
      handleDrawerClose,
    ],
  );

  return {
    // state
    drawerOpen,
    currentRow,
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    actionMode,
    sortColumn,
    sortDirection,
    filterValues,
    drawerContentRef,
    deleteDialogOpen,
    deleteConfirmText,
    setDeleteConfirmText,
    isDeleting: deleteMutation.isPending,
    // data
    organizations,
    filteredOrganizations,
    totalItems,
    totalPages,
    isLoading,
    error,
    organizationFormDefaultValues,
    // handlers
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleDrawerClose,
    handleSubmit,
    openAddDrawer,
    openEditDrawer,
    openDeleteDialog,
    closeDeleteDialog,
    handleDelete,
  };
}
